import { useState, useEffect } from 'react';
import { Settings2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { apiClient } from '../lib/apiClient';
import { SearchableSelect } from './SearchableSelect';
import { JobSiteDevices } from './JobSiteDevices';

interface JobSiteRow {
  id: string;
  name: string;
  active: boolean | null;
  aspire_property_id: number | null;
}

interface JobSiteOption {
  id: string;
  name: string;
  propertyNumber: number | null;
}

/**
 * Standalone property device management for field techs (User role) and up.
 *
 * Lets a tech configure a property's controllers/zones, double-checks and water
 * meters BEFORE going on site, without opening (and polluting) an inspection
 * report. Reuses the exact same {@link JobSiteDevices} UI shown on the in-
 * inspection "Manage Devices" tab. Because no report is created and the
 * inspection workflow (with its location heartbeat / live-activity tracking) is
 * never mounted here, pre-trip device setup no longer shows up as live activity.
 * (Requested feature 2634faf4.)
 */
export function PropertyDeviceManagement() {
  const { t } = useTranslation('inspection');
  const [jobSites, setJobSites] = useState<JobSiteOption[]>([]);
  const [selectedJobSiteId, setSelectedJobSiteId] = useState<string>('');

  useEffect(() => {
    (async () => {
      try {
        const all = await apiClient.get<JobSiteRow[]>('/api/job-sites');
        const active = all
          .filter((s) => s.active !== false)
          .map((s) => ({ id: s.id, name: s.name, propertyNumber: s.aspire_property_id }))
          .sort((a, b) => a.name.localeCompare(b.name));
        setJobSites(active);
      } catch (err) {
        console.error('Failed to load job sites:', err);
      }
    })();
  }, []);

  const selectedSite = jobSites.find((s) => s.id === selectedJobSiteId) ?? null;

  return (
    <div className="space-y-5 text-theme-primary">
      <div className="flex items-start gap-3">
        <div className="p-2 rounded-lg bg-blue-50 text-blue-600 dark:bg-blue-500/10 dark:text-blue-400 shrink-0">
          <Settings2 size={22} />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-theme-primary leading-tight">{t('manageDevices.title')}</h1>
          <p className="text-sm text-theme-secondary">{t('manageDevices.subtitle')}</p>
        </div>
      </div>

      <div className="max-w-md">
        <label className="block text-[11px] font-semibold text-theme-secondary mb-1 uppercase tracking-wide">
          {t('manageDevices.propertyLabel')}
        </label>
        <SearchableSelect
          value={selectedJobSiteId}
          onChange={setSelectedJobSiteId}
          options={jobSites.map((site) => ({
            value: site.id,
            label: site.name,
            searchText: `${site.name} ${site.propertyNumber ?? ''}`,
          }))}
          placeholder={t('manageDevices.propertyPlaceholder')}
          noResultsHint={t('setup.propertyNotFoundAspireHint')}
          className="w-full px-3 py-2.5 text-sm bg-surface text-theme-primary border border-line rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        />
      </div>

      {selectedSite ? (
        <div className="border-t border-line pt-5">
          <JobSiteDevices jobSiteId={selectedSite.id} jobSiteName={selectedSite.name} />
        </div>
      ) : (
        <div className="flex items-center justify-center text-center text-sm text-theme-secondary border border-dashed border-line rounded-xl px-6 py-16">
          {t('manageDevices.selectPrompt')}
        </div>
      )}
    </div>
  );
}
